/* eslint-disable react-hooks/exhaustive-deps */
import { Actions, Async, F0, F1, FSpread } from '@client/new/helpers/types';
import { useUpdate } from '@client/new/helpers/useUpdate';
import _ from 'lodash';
import { Folder, minFileSizeWarning } from '../models/videos.domain';
import {
  toListItem,
  getFooterButtons,
  ButtonVariant,
  toFolderItem,
} from '../components/addFromLibrary/mappers';
import {
  UploadToLibraryModel,
  useUploadToLibraryModel,
} from '../models/uploadToLibrary.model';
import { useEffect, useMemo } from 'react';
import { ButtonColor } from '@design-system/components/button/button.css';
import { nameMatches } from './mappers';
import {
  contentFilter,
  ContentFilters,
  getFoldersChildrenCount,
  getFoldersScope,
  getVideosScope,
  sortList,
} from '@client/new/common/library/helpers';
import { getFileNameWihoutExtension } from '@client/new/common/videos/helpers';
import { FolderListItem } from '../components/addFromLibrary/libraryList';
import { useLibraryModel } from '../../creator/library/models/library.model';
import { useInfluencerId } from '@client/new/contexts/InfluencerIdContext';

export type SelectTab = 'Library' | 'Upload';
const tabs: Array<SelectTab> = ['Library', 'Upload'];
export type Modal = ContentFilters | 'None';

type PayloadMap = {
  close: never;
  search: string;
  selectTab: SelectTab;
  selectVideo: string;
  addVideo: never;
  onDrop: { file: File; videoType: ContentFilters | null };
  showSelectThumbnail: never;
  showEditName: never;
  upload: never;
  closeConfirmation: never;
  cancel: never;
  confirmCancel: never;
  editName: string;
  thumbnailTimestamp: number;
  cancelUpload: never;
  openFolder: number | null;
  filterVideos: ContentFilters;
  toggleViewSize: null;
  setCustomThumbnail: string;
};
export type VideosVMAction = Actions<PayloadMap>;
export type AddFromLibraryVMDispatcher = FSpread<PayloadMap>;
export type UploadStep = 'Name' | 'Thumbnail' | 'Uploading' | 'Processing';

type VideosVMState = {
  searchText: string;
  activeTab: SelectTab;
  selectedIds: string[] | null;
  uploadStep: UploadStep;
  confirmModal: boolean;
  currentFolder: null | Folder;
  filterVideos: ContentFilters | null;
  smallerView: boolean;
  customThumbnailUrl: string;
};

const initialState: VideosVMState = {
  searchText: '',
  activeTab: 'Library',
  selectedIds: null,
  uploadStep: 'Name',
  confirmModal: false,
  currentFolder: null,
  filterVideos: null,
  smallerView: false,
  customThumbnailUrl: '',
};

export type VideoListItem = {
  id: string;
  imageUrl: string;
  title: string;
  duration: string;
  tag: {
    label: string;
  };
  date: string;
  onClick: () => void;
  tags?: string[];
};

export type AddFromLibaryVM = Pick<VideosVMState, 'activeTab'> & {
  dispatch: AddFromLibraryVMDispatcher;
  isMultipleChoice: boolean;
  library: {
    folders: Array<FolderListItem>;
    videos: Array<VideoListItem>;
    currentFolder: (Folder & { count: string }) | null;
    selectedIds: VideosVMState['selectedIds'];
    searchText: VideosVMState['searchText'];
    actionsDisabled: boolean;
    activeVideoFilter: ContentFilters | null;
    filterVideos: (type: ContentFilters) => void;
    backFromCurrentFolder: () => void;
    selectVideo: (id: string) => void;
    search: (value: string) => void;
  };
  upload: {
    hasCustomThumbnail: boolean;
    showDropZone: boolean;
    showPreview: boolean;
    file: UploadToLibraryModel['uploadFile'];
    step: VideosVMState['uploadStep'];
    thumbnailTimestamp: UploadToLibraryModel['thumbnailTimestamp'];
    progress: number;
    isUploading: boolean;
    uploadSizeWarning?: boolean;
    validationError: UploadToLibraryModel['validationError'];
    customThumbnailUrl: string;
  };
  confirmationModal: { open: boolean };
  footerButtons: Array<{
    props: {
      color: ButtonColor;
      variant: ButtonVariant;
      onClick: () => void;
      disabled?: boolean;
    };
    label: string;
  }>;
  tabs: Array<'Library' | 'Upload'>;
  videoType: ContentFilters | null;
  smallerView: boolean;
};

type AddVideoPayload = {
  uuid: string;
  title: string;
  thumbnailTimestampMs: number;
  thumbnailCustomURL?: string;
};

type AddFromLibraryVMParams = {
  onClose: F0;
  type: ContentFilters | null;
  action: 'add' | 'update';
  onAddVideo: F1<AddVideoPayload>;
  onAddVideoFromSelection: F1<AddVideoPayload[]>;
  isMultipleChoice: boolean;
  skipProcessingStep?: boolean;
  allowCustomThumbnails?: boolean;
};

export const useAddFromLibraryVM = (
  p: AddFromLibraryVMParams,
): Async<AddFromLibaryVM> => {
  const influencerId = useInfluencerId();

  const [state, updateState] = useUpdate(initialState);

  const model = useUploadToLibraryModel({
    influencerId,
    onAddVideo: (v) =>
      p.onAddVideo({ ...v, thumbnailCustomURL: state.customThumbnailUrl }),
    type: p.type,
    skipProcessing: p.skipProcessingStep,
  });

  const library = useLibraryModel({ creatorId: influencerId });

  useEffect(() => {
    updateState({ filterVideos: p.type });
  }, []);

  useEffect(() => {
    if (state.uploadStep !== 'Uploading' && model.isUploading) {
      updateState({ uploadStep: 'Uploading' });
    }
  }, [model.isUploading, state.uploadStep]);

  useEffect(() => {
    if (state.uploadStep !== 'Processing' && model.isProcessing) {
      updateState({ uploadStep: 'Processing' });
    }
  }, [model.isProcessing, state.uploadStep]);

  useEffect(() => {
    if (model.isUploadFinished) {
      updateState(initialState);
      p.onClose();
    }
  }, [model.isUploadFinished]);

  const filteredVideos = useMemo(() => {
    if (library.type !== 'fetched') return [];
    return library.value.videos
      .filter((v) => contentFilter(v, state.filterVideos) && !v.isProcessing)
      .filter(nameMatches(state.searchText))
      .sort((a, b) => sortList(a, b, 'updatedAt', false));
  }, [library.type, state.filterVideos, state.searchText]);

  const filteredFolders = useMemo(() => {
    if (library.type !== 'fetched') return [];
    return library.value.folders
      .filter(nameMatches(state.searchText))
      .sort((a, b) => sortList(a, b, 'updatedAt', false));
  }, [library.type, state.searchText]);

  if (library.type !== 'fetched') return library;

  const dispatch: AddFromLibraryVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as VideosVMAction;
    switch (action.type) {
      case 'close': {
        if (!model.uploadFile) {
          return updateState(initialState);
        }
        return updateState({ confirmModal: true });
      }
      case 'search':
        return updateState({ searchText: action.payload });
      case 'selectTab':
        return updateState({ activeTab: action.payload });
      case 'selectVideo':
        selectVideo(action.payload);
        return null;
      case 'addVideo': {
        if (state.selectedIds) {
          const payload = state.selectedIds.map((uuid) => {
            const v = library.value.videos.find((i) => i.id === uuid);
            return {
              uuid,
              title: v?.name || 'N/A',
              thumbnailTimestampMs: 0,
            };
          });

          p.onAddVideoFromSelection(payload);
          updateState(initialState);
        }
        return null;
      }
      case 'showSelectThumbnail':
        return updateState({ uploadStep: 'Thumbnail' });
      case 'showEditName':
        return updateState({ uploadStep: 'Name' });
      case 'closeConfirmation':
        return updateState({ confirmModal: false });
      case 'cancel': {
        if (!model.uploadFile) {
          return updateState(initialState);
        }
        return updateState({ confirmModal: true });
      }
      case 'cancelUpload':
        model.dispatch('cancel');
        return updateState(initialState);
      case 'confirmCancel': {
        model.dispatch('cancel');
        return updateState(initialState);
      }
      case 'upload':
        model.dispatch(action.type);
        return null;
      case 'onDrop':
      case 'editName':
      case 'thumbnailTimestamp':
        model.dispatch(action.type, action.payload);
        return null;
      case 'openFolder':
        return updateState({
          currentFolder:
            action.payload === null
              ? null
              : library.value.folders.find((f) => f.id === action.payload),
        });
      case 'filterVideos':
        return updateState({
          filterVideos:
            action.payload === state.filterVideos ? null : action.payload,
        });
      case 'toggleViewSize':
        return updateState((prev) => ({
          ...prev,
          smallerView: !prev.smallerView,
        }));

      case 'setCustomThumbnail':
        return updateState({ customThumbnailUrl: action.payload });
    }
  };

  const selectVideo = (id: string) => {
    if ((p.type && p.action === 'update') || !p.isMultipleChoice) {
      updateState({ selectedIds: [id] });
      return;
    }
    const mappedIds = !state.selectedIds?.includes(id)
      ? [...(state.selectedIds || []), id]
      : state.selectedIds.filter((i) => i !== id);

    updateState({ selectedIds: mappedIds });
  };

  const scopedVideos = getVideosScope(
    filteredVideos,
    state.currentFolder?.id || null,
    state.searchText,
  );

  const scopedFolders = getFoldersScope(
    filteredFolders,
    state.currentFolder?.id || null,
    state.searchText,
  );

  const videos = scopedVideos.map((v) =>
    toListItem(v, () => dispatch('selectVideo', v.id)),
  );

  const folders = scopedFolders.map((item) =>
    toFolderItem({
      item,
      ...library.value,
      onClick: () => dispatch('openFolder', item.id),
    }),
  );

  const actionsDisabled = _.isEmpty(videos);

  const isUploadNameEmpty = model.uploadFile
    ? getFileNameWihoutExtension(model.uploadFile.name).trim().length === 0
    : false;

  return {
    type: 'fetched',
    value: {
      isMultipleChoice: p.isMultipleChoice,
      activeTab: state.activeTab,
      dispatch,
      tabs,
      library: {
        videos,
        folders: folders,
        currentFolder: !state.currentFolder
          ? null
          : {
              ...state.currentFolder,
              count: getFoldersChildrenCount(
                library.value.folders,
                library.value.videos,
                state.currentFolder.id,
              ),
            },
        selectedIds: state.selectedIds,
        searchText: state.searchText,
        actionsDisabled,
        activeVideoFilter: state.filterVideos,
        filterVideos: (type: ContentFilters) => dispatch('filterVideos', type),
        backFromCurrentFolder: () =>
          dispatch('openFolder', state.currentFolder?.parentId || null),
        selectVideo: (id: string) => dispatch('selectVideo', id),
        search: (value: string) => dispatch('search', value),
      },
      upload: {
        hasCustomThumbnail: p.allowCustomThumbnails || false,
        showDropZone: state.activeTab === 'Upload' && !model.uploadFile,
        showPreview: state.activeTab === 'Upload' && model.uploadFile !== null,
        file: model.uploadFile,
        step: state.uploadStep,
        thumbnailTimestamp: model.thumbnailTimestamp,
        progress: model.progress,
        isUploading: model.isUploading,
        uploadSizeWarning: !!(
          model.uploadFile && model.uploadFile.size > minFileSizeWarning
        ),
        validationError: model.validationError,
        customThumbnailUrl: state.customThumbnailUrl,
      },
      confirmationModal: {
        open: state.confirmModal,
      },
      footerButtons: getFooterButtons({
        disabled: !!model.validationError || isUploadNameEmpty,
        activeTab: state.activeTab,
        dispatch,
        selectedIds: state.selectedIds,
        actionsDisabled,
        uploadStep: state.uploadStep,
        inProgress:
          state.uploadStep === 'Uploading' || state.uploadStep === 'Processing',
        closeModal: p.onClose,
      }),
      videoType: p.type,
      smallerView: state.smallerView,
    },
  };
};
